import {
  Event as E,
  Filter,
  GanttFilter,
  Identifiable,
  List,
  Milestone,
  Project,
  ProjectElementType,
  Sort,
  Tag,
  Task,
} from "@planzone/dto";
import {
  State,
  ListPE,
  TaskPE,
  EventPE,
  ProjectPE,
  MilestonePE,
} from "./types";

function areArrayEqual(first: string[], second: string[]): boolean {
  const set = new Set([...first, ...second]);

  return set.size === first.length && set.size === second.length;
}

function hasSameKeys(first: Filter, second: Filter): boolean {
  return areArrayEqual(Object.keys(first), Object.keys(second));
}

function extractId<I extends Object, K extends keyof I>(
  col: I[],
  keyName: K,
): I[K][] {
  return col.map((o) => o[keyName]);
}

function isTagList(key: string): boolean {
  return (
    key === "projectsElementsTags" ||
    key === "projectsTags" ||
    key === "resourceTags"
  );
}

function areFiltersEqual(first: Filter, second: Filter): boolean {
  if (!hasSameKeys(first, second)) {
    return false;
  }

  return Object.entries(first).reduce(
    (areEqual, [key, value]) =>
      !areEqual || !(key in second)
        ? false
        : isTagList(key)
          ? areArrayEqual(
              extractId(value as Tag[], "name"),
              extractId(second[key as keyof typeof second] as Tag[], "name"),
            )
          : areArrayEqual(
              extractId(value as Identifiable<any>[], "uuid"),
              extractId(
                second[key as keyof typeof second] as Identifiable<any>[],
                "uuid",
              ),
            ),
    true,
  );
}

function matchRequest(
  state: State,
  { scope, elementTypes, ...filter }: GanttFilter,
  sort: Sort,
  size: number,
): number {
  return state.findIndex((req) => {
    const {
      scope: reqScope,
      elementTypes: reqElementTypes,
      ...reqFilter
    } = req.filter;

    return (
      req.sort === sort &&
      req.size === size &&
      areFiltersEqual(reqFilter, filter) &&
      ((typeof elementTypes === "undefined" &&
        typeof reqElementTypes === "undefined") ||
        (Array.isArray(elementTypes) &&
          Array.isArray(reqElementTypes) &&
          areArrayEqual(elementTypes, reqElementTypes))) &&
      reqScope === scope
    );
  });
}

function projectToProjectElement(
  project: Identifiable<Project>,
  inject: (projectElement: ProjectPE) => Partial<ProjectPE>,
): ProjectPE {
  const el: ProjectPE = {
    type: ProjectElementType.PROJECT,
    uuid: project.uuid,
    parentUuid: "",
    name: project.name,
    description: project.description,
    startDate: project.rootList.startDate,
    endDate: project.rootList.endDate,
    referenceKeyRank: 0,
    tags: project.tags,
    keyRank: 0,
    depth: 0,
    percentComplete: 0,
    completionDate: "",
    attributes: project.attributes || {},
    activityRootUuid: project.rootList.uuid,
    zone: project.zone,
    parentUuids: [],
  };

  return { ...el, ...inject(el) };
}

function listToProjectElement(
  list: Identifiable<List>,
  inject: (projectElement: ListPE) => Partial<ListPE>,
): ListPE {
  const el: ListPE = {
    type: ProjectElementType.LIST,
    uuid: list.uuid,
    parentUuid: list.parentList ? list.parentList.uuid : "",
    name: list.name,
    description: list.description,
    startDate: list.startDate,
    endDate: list.endDate,
    referenceKeyRank: list.activityKeyRank,
    tags: list.tags,
    keyRank: 0,
    depth: 0,
    percentComplete: 0,
    completionDate: "",
    attributes: list.attributes || {},
    parentUuids: [],
    activityRootUuid: "",
    minStartDate: list.minStartDate,
    maxStartDate: list.maxStartDate,
    minEndDate: list.minEndDate,
    maxEndDate: list.maxEndDate,
  };

  return { ...el, ...inject(el) };
}

function taskToProjectElement(
  task: Identifiable<Task>,
  inject: (projectElement: TaskPE) => Partial<TaskPE>,
): TaskPE {
  const el: TaskPE = {
    type: ProjectElementType.TASK,
    uuid: task.uuid,
    name: task.name,
    description: task.description,
    startDate: task.startDate,
    endDate: task.endDate,
    referenceKeyRank: task.activityKeyRank,
    tags: task.tags,
    allocations: task.allocations,
    status: task.status,
    percentComplete: task.percentComplete,
    completionDate: task.completionDate,
    attributes: task.attributes || {},
    depth: 0,
    keyRank: 0,
    parentUuid: "",
    activityRootUuid: "",
    parentUuids: [],
    minStartDate: task.minStartDate,
    maxEndDate: task.maxEndDate,
  };

  return { ...el, ...inject(el) };
}

function milestoneToProjectElement(
  milestone: Identifiable<Milestone>,
  inject: (projectElement: MilestonePE) => Partial<MilestonePE>,
): MilestonePE {
  const el: MilestonePE = {
    type: ProjectElementType.MILESTONE,
    uuid: milestone.uuid,
    name: milestone.name,
    description: milestone.description,
    startDate: milestone.endDate,
    endDate: milestone.endDate,
    referenceKeyRank: milestone.keyRank,
    tags: milestone.tags,
    state: milestone.state,
    percentComplete: milestone.state === "DONE" ? 100 : 0,
    completionDate: milestone.completedAt,
    attributes: milestone.attributes || {},
    depth: 0,
    keyRank: 0,
    parentUuid: "",
    activityRootUuid: "",
    parentUuids: [],
    minStartDate: milestone.minStartDate,
    maxEndDate: milestone.maxEndDate,
  };

  return { ...el, ...inject(el) };
}

function eventToProjectElement(
  event: Identifiable<E>,
  inject: (projectElement: EventPE) => Partial<EventPE>,
): EventPE {
  const endTime = !!event.endTime ? new Date(event.endTime) : new Date();
  const el: EventPE = {
    type: ProjectElementType.EVENT,
    uuid: event.uuid,
    name: event.name,
    description: event.description,
    startDate: event.startTime,
    endDate: event.endTime,
    referenceKeyRank: event.keyRank,
    tags: event.tags,
    percentComplete: !!event.endTime && endTime < new Date() ? 100 : 0,
    completionDate: event.endTime,
    attributes: event.attributes || {},
    parentUuid: "",
    activityRootUuid: "",
    keyRank: 0,
    depth: 0,
    parentUuids: [],
    minStartDate: event.minStartDate,
    maxEndDate: event.maxEndDate,
  };

  return { ...el, ...inject(el) };
}

export {
  matchRequest,
  projectToProjectElement,
  listToProjectElement,
  taskToProjectElement,
  milestoneToProjectElement,
  eventToProjectElement,
};
